'use client';

import { FC } from 'react';
import { Content } from '@prismicio/client';
import { SliceComponentProps, PrismicRichText } from '@prismicio/react';
import { PrismicNextImage } from '@prismicio/next';
import clsx from 'clsx';

export type FeatureOverviewGridProps =
  SliceComponentProps<Content.FeatureOverviewGridSlice>;

// Type for the leftAlignedWithBackground variation
interface LeftAlignedVariation {
  background?: {
    url?: string;
    alt?: string;
  };
}

const FeatureOverviewGrid: FC<FeatureOverviewGridProps> = ({ slice }) => {
  const { heading, description, features } = slice.primary;
  const isLeftAlignedVariation =
    slice.variation === 'leftAlignedWithBackground';
  const backgroundImage = isLeftAlignedVariation
    ? (slice.primary as typeof slice.primary & LeftAlignedVariation).background
    : null;

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className={clsx(
        'section-base',
        isLeftAlignedVariation ? 'left-aligned-gradient' : 'default-background',
      )}
    >
      {/* Background Image for leftAlignedWithBackground variation */}
      {isLeftAlignedVariation && backgroundImage && (
        <>
          <PrismicNextImage
            field={backgroundImage}
            fill
            style={{ objectFit: 'cover' }}
            className="absolute inset-0 z-0"
            quality={85}
          />
          <div className="left-aligned-overlay" />
          {/* Top Gradient Fade */}
          <div className="absolute top-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-[#0E1010] to-transparent pointer-events-none" />
          {/* Bottom Gradient Fade */}
          <div className="absolute bottom-0 left-0 w-full h-52 md:h-52 z-20 bg-gradient-to-b from-transparent to-[#0E1010] pointer-events-none" />
        </>
      )}
      <div className="container mx-auto max-w-7xl relative z-30 pt-8 md:pt-12 px-4 md:px-6 lg:px-8">
        {isLeftAlignedVariation ? (
          <div className="mb-8 md:mb-12 lg:mb-16 text-left max-w-4xl px-4 md:px-0">
            <PrismicRichText
              field={heading}
              components={{
                heading1: ({ children }: { children: React.ReactNode }) => (
                  <h2
                    className={clsx(
                      'text-3xl md:text-4xl lg:text-5xl font-bold text-white leading-tight mb-4 md:mb-6',
                      'font-antonio',
                    )}
                  >
                    {children}
                  </h2>
                ),
              }}
            />
            <div className="mb-4">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }: { children: React.ReactNode }) => (
                    <p className="text-gray-300 leading-relaxed font-inter text-sm md:text-base">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          </div>
        ) : (
          <div className="mb-8 grid grid-cols-1 lg:grid-cols-2 gap-6 items-start">
            <div className="text-left">
              <PrismicRichText
                field={heading}
                components={{
                  heading1: ({ children }: { children: React.ReactNode }) => (
                    <h2
                      className={clsx(
                        'font-[700] text-[42px] leading-[120%] tracking-[0%] text-white',
                        'font-antonio',
                      )}
                    >
                      {children}
                    </h2>
                  ),
                }}
              />
            </div>
            <div className="text-left">
              <PrismicRichText
                field={description}
                components={{
                  paragraph: ({ children }: { children: React.ReactNode }) => (
                    <p className="font-inter font-normal text-[16px] leading-[150%] tracking-[0%] text-gray-300">
                      {children}
                    </p>
                  ),
                }}
              />
            </div>
          </div>
        )}

        <div
          className={`relative overflow-visible ${
            isLeftAlignedVariation
              ? 'h-[1120px] sm:h-[750px] md:h-[660px] lg:h-[630px] xl:h-[680px]'
              : 'h-[1120px] sm:h-[750px] md:h-[660px] lg:h-[630px] xl:h-[680px]'
          }`}
        >
          {features.map((feature, index) => {
            const cardNumber = index + 1;
            const cardClassName = isLeftAlignedVariation
              ? `feature-card-variation${cardNumber}`
              : `feature-card${cardNumber}`;

            return (
              <div
                key={index}
                className={`
                ${cardClassName}
                absolute transition-all duration-300 ease-in-out md:hover:scale-105 md:hover:-translate-y-2 overflow-hidden
                ${index % 2 === 0 ? 'rounded-none' : 'rounded-[15px]'}
                `.trim()}
                style={{
                  boxShadow: '0 0 15px rgba(0, 255, 127, 0.5)',
                }}
              >
                <div className="flex justify-between items-center px-3 py-1 bg-[#0E1010] border-b border-[#00FF7F]/30">
                  <div className="flex space-x-2">
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: '#00FF7F' }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: '#00FF7F' }}
                    ></div>
                    <div
                      className="w-1.5 h-1.5 rounded-full"
                      style={{ background: '#00FF7F' }}
                    ></div>
                  </div>
                  <div className="text-sm text-white font-medium tracking-tight"></div>
                  <div className="flex space-x-3">
                    <div
                      className="w-3 h-3 border border-current"
                      style={{ color: '#00FF7F', borderWidth: '1.5px' }}
                    ></div>
                    <div
                      className="w-3 h-3 relative"
                      style={{ color: '#00FF7F' }}
                    >
                      <div
                        className="absolute inset-0"
                        style={{
                          background: `linear-gradient(45deg, transparent 48%, currentColor 48%, currentColor 52%, transparent 52%),
                                     linear-gradient(-45deg, transparent 48%, currentColor 48%, currentColor 52%, transparent 52%)`,
                        }}
                      ></div>
                    </div>
                  </div>
                </div>
                <div
                  className="p-6 rounded-[6px]"
                  style={{
                    background: `linear-gradient(180deg, rgba(14, 16, 16, 1) 0%, rgba(23, 94, 82, 0.2) 21.47%, rgba(14, 16, 16, 0) 100%)`,
                  }}
                >
                  <PrismicRichText
                    field={feature.title}
                    components={{
                      heading3: ({
                        children,
                      }: {
                        children: React.ReactNode;
                      }) => (
                        <h3
                          className={clsx(
                            'font-bold mb-4 text-white',
                            'font-antonio',
                            isLeftAlignedVariation
                              ? 'text-[24px] sm:text-[26px] md:text-[28px] lg:text-[30px] xl:text-[31px] leading-[110%] tracking-[0%]'
                              : 'text-[31px] leading-[100%] tracking-[0%]',
                          )}
                        >
                          {children}
                        </h3>
                      ),
                    }}
                  />
                  <PrismicRichText
                    field={feature.text}
                    components={{
                      paragraph: ({
                        children,
                      }: {
                        children: React.ReactNode;
                      }) => (
                        <p
                          className={`font-inter font-normal text-white ${
                            isLeftAlignedVariation
                              ? 'text-[14px] sm:text-[15px] md:text-[16px] lg:text-[17px] xl:text-[18px] leading-[140%] tracking-[0]'
                              : 'text-[18px] leading-[140%] tracking-[0]'
                          }`}
                        >
                          {children}
                        </p>
                      ),
                    }}
                  />
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Styled-jsx for component-specific styles - automatically code-split */}
      <style jsx>{`
        .section-base {
          position: relative;
          overflow: hidden;
        }

        .default-background {
          background-color: #0e1010;
        }

        .left-aligned-gradient {
          background: linear-gradient(
            180deg,
            #0e1010 0.06%,
            rgba(14, 16, 16, 0.9) 35.06%,
            rgba(14, 16, 16, 0.8) 50.06%,
            rgba(14, 16, 16, 0.9) 75.06%,
            #0e1010 100.06%
          );
        }

        .left-aligned-overlay {
          position: absolute;
          inset: 0;
          z-index: 5;
          pointer-events: none;
          background: linear-gradient(
            180deg,
            #0e1010 0.06%,
            rgba(14, 16, 16, 0.9) 35.06%,
            rgba(14, 16, 16, 0.8) 50.06%,
            rgba(14, 16, 16, 0.9) 75.06%,
            #0e1010 100.06%
          );
        }

        /* Base card styles */
        .feature-card1,
        .feature-card2,
        .feature-card3,
        .feature-card4,
        .feature-card-variation1,
        .feature-card-variation2,
        .feature-card-variation3,
        .feature-card-variation4 {
          background: rgba(17, 24, 20, 0.75);
          border: 1px solid #00ff7f;
          backdrop-filter: blur(10px);
        }

        /* Mobile positioning */
        .feature-card1,
        .feature-card-variation1 {
          top: 80px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100vw - 40px);
          max-width: calc(100vw - 40px);
          height: 240px;
        }

        .feature-card2,
        .feature-card-variation2 {
          top: 340px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100vw - 40px);
          max-width: calc(100vw - 40px);
          height: 240px;
        }

        .feature-card3,
        .feature-card-variation3 {
          top: 600px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100vw - 40px);
          max-width: calc(100vw - 40px);
          height: 240px;
        }

        .feature-card4,
        .feature-card-variation4 {
          top: 860px;
          left: 50%;
          transform: translateX(-50%);
          width: calc(100vw - 40px);
          max-width: calc(100vw - 40px);
          height: 240px;
        }

        /* Responsive: sm (640px+) */
        @media (min-width: 640px) {
          .feature-card1,
          .feature-card-variation1 {
            top: 100px;
            left: 20px;
            transform: none;
            width: 222px;
            max-width: 222px;
            height: 351px;
          }
          .feature-card2,
          .feature-card-variation2 {
            top: 97px;
            left: 240px;
            transform: none;
            width: 220px;
            max-width: 220px;
            height: 351px;
          }
          .feature-card3,
          .feature-card-variation3 {
            top: 99px;
            left: 458px;
            transform: none;
            width: 220px;
            max-width: 220px;
            height: 351px;
          }
          .feature-card4,
          .feature-card-variation4 {
            top: 438px;
            left: 219px;
            transform: none;
            width: 260px;
            max-width: 260px;
            height: 267px;
          }
        }

        /* Responsive: md (768px+) */
        @media (min-width: 768px) {
          .feature-card1,
          .feature-card-variation1 {
            top: 120px;
            left: 20px;
            width: 230px;
            max-width: 230px;
            height: 280px;
          }
          .feature-card2,
          .feature-card-variation2 {
            top: 296px;
            left: 235px;
            width: 260px;
            max-width: 260px;
            height: 300px;
          }
          .feature-card3,
          .feature-card-variation3 {
            top: 60px;
            left: 414px;
            width: 240px;
            max-width: 240px;
            height: 300px;
          }
          .feature-card4,
          .feature-card-variation4 {
            top: 331px;
            left: 543px;
            width: 260px;
            max-width: 260px;
            height: 280px;
          }
          .feature-card-variation1 {
            height: 300px;
          }
          .feature-card-variation2 {
            height: 275px;
          }
        }

        /* Responsive: lg (1024px+) */
        @media (min-width: 1024px) {
          .feature-card1,
          .feature-card-variation1 {
            top: 160px;
            left: 30px;
            width: 220px;
            max-width: 220px;
            height: 280px;
          }
          .feature-card2,
          .feature-card-variation2 {
            top: 296px;
            left: 235px;
            width: 260px;
            max-width: 260px;
            height: 300px;
          }
          .feature-card3,
          .feature-card-variation3 {
            top: 80px;
            left: 491px;
            width: 260px;
            max-width: 260px;
            height: 320px;
          }
          .feature-card4,
          .feature-card-variation4 {
            top: 300px;
            left: 731px;
            width: 260px;
            max-width: 260px;
            height: 272px;
          }
          .feature-card-variation1 {
            height: 340px;
          }
          .feature-card-variation2 {
            height: 250px;
          }
          .feature-card-variation3 {
            height: 300px;
          }
          .feature-card-variation4 {
            height: 290px;
          }
        }

        /* Responsive: xl (1280px+) */
        @media (min-width: 1280px) {
          .feature-card1,
          .feature-card-variation1 {
            top: 200px;
            left: 40px;
            width: 280px;
            max-width: 280px;
            height: 335px;
          }
          .feature-card2,
          .feature-card-variation2 {
            top: 300px;
            left: 304px;
            width: 320px;
            max-width: 320px;
            height: 335px;
          }
          .feature-card3,
          .feature-card-variation3 {
            top: 110px;
            left: 609px;
            width: 320px;
            max-width: 320px;
            height: 335px;
          }
          .feature-card4,
          .feature-card-variation4 {
            top: 335px;
            left: 914px;
            width: 328px;
            max-width: 328px;
            height: 315px;
          }
          .feature-card-variation1 {
            height: 340px;
          }
        }
      `}</style>
    </section>
  );
};

export default FeatureOverviewGrid;
